import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.balancebags.com/nutritionist';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and cache-busting headers to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nutritionist_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Prevent browser/CDN caching to ensure fresh data on each request
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    // Add timestamp to GET requests to bust any URL-based caching
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses - clear all cached data to prevent data leakage
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear all nutritionist-related localStorage keys
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('nutritionist_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      sessionStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/api/login', { email, password });
    return response.data;
  },

  logout: async (refreshToken) => {
    try {
      // Call backend to blacklist the token server-side
      const response = await api.post('/api/v1/auth/logout', { refresh: refreshToken });
      return response.data;
    } catch (err) {
      // Don't block logout if API call fails
      console.error('Logout API error:', err);
      return { success: false };
    }
  },

  getMe: async () => {
    const response = await api.get('/api/me');
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/api/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Dashboard APIs
export const dashboardApi = {
  getStats: async () => {
    const response = await api.get('/api/stats');
    return response.data;
  },
};

// Queue/Thread APIs
export const threadApi = {
  getQueue: async (status = null, type = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (type) params.append('type', type);
    const response = await api.get(`/api/queue?${params.toString()}`);
    return response.data;
  },

  getThread: async (referenceId) => {
    const response = await api.get(`/api/thread/${referenceId}`);
    return response.data;
  },

  replyToThread: async (referenceId, data) => {
    const response = await api.post(`/api/thread/${referenceId}/reply`, data);
    return response.data;
  },

  closeThread: async (referenceId, reason) => {
    const response = await api.post(`/api/thread/${referenceId}/close`, { reason });
    return response.data;
  },
};

// Users APIs
export const usersApi = {
  getUsers: async (hasOpenThread = null) => {
    const params = new URLSearchParams();
    if (hasOpenThread !== null) {
      params.append('has_open_thread', hasOpenThread.toString());
    }
    const response = await api.get(`/api/users?${params.toString()}`);
    return response.data;
  },

  // Get single user with full profile including current_meal_plan_recipes
  // GET /nutritionist/api/users/<user_id>
  getUser: async (userId) => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  },

  createCheckin: async (userId, message) => {
    const response = await api.post(`/api/users/${userId}/create-checkin`, { message });
    return response.data;
  },

  // Create initial evaluation for a user
  // POST /nutritionist/api/users/<user_id>/create-evaluation
  createEvaluation: async (userId, message) => {
    const response = await api.post(`/api/users/${userId}/create-evaluation`, { message });
    return response.data;
  },

  // Get recommended templates for a user
  // GET /nutritionist/api/users/<user_id>/templates
  getTemplates: async (userId) => {
    const response = await api.get(`/api/users/${userId}/templates`);
    return response.data;
  },

  // Get unified timeline of all threads/conversations with a user
  // GET /nutritionist/api/users/<user_id>/timeline
  getTimeline: async (userId) => {
    const response = await api.get(`/api/users/${userId}/timeline`);
    return response.data;
  },
};

// Template APIs
export const templateApi = {
  // Get all templates (supports ?category= filter)
  // GET /nutritionist/api/templates
  getAll: async (category = null) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const response = await api.get(`/api/templates?${params.toString()}`);
    return response.data;
  },

  // Get a specific template
  // GET /nutritionist/api/templates/<id>
  get: async (templateId) => {
    const response = await api.get(`/api/templates/${templateId}`);
    return response.data;
  },

  // Render a template with user data (returns message with placeholders filled)
  // POST /nutritionist/api/templates/<id>/render
  // Pass user_id to replace placeholders like [Name], [allergies], [health_goal]
  render: async (templateId, { referenceId = null, userId = null } = {}) => {
    const body = {};
    if (referenceId) body.reference_id = referenceId;
    else if (userId) body.user_id = userId;
    const response = await api.post(`/api/templates/${templateId}/render`, body);
    return response.data;
  },
};

// Recipe APIs
export const recipeApi = {
  // Get full recipe detail
  // GET /nutritionist/api/recipes/<id>
  get: async (recipeId) => {
    const response = await api.get(`/api/recipes/${recipeId}`);
    return response.data;
  },
};

// Thread status and type constants
export const THREAD_STATUS = {
  AWAITING_NUTRITIONIST: 'AWAITING_NUTRITIONIST',
  AWAITING_USER: 'AWAITING_USER',
  CLOSED: 'CLOSED',
};

export const THREAD_TYPE = {
  COMPLEX_CASE_EVALUATION: 'COMPLEX_CASE_EVALUATION',
  WEEKLY_CHECKIN: 'WEEKLY_CHECKIN',
};

export const getStatusDisplay = (status) => {
  const displays = {
    AWAITING_NUTRITIONIST: { label: 'Needs Your Response', color: 'warning' },
    AWAITING_USER: { label: 'Awaiting User', color: 'info' },
    CLOSED: { label: 'Closed', color: 'default' },
  };
  return displays[status] || { label: status, color: 'default' };
};

export const getTypeDisplay = (type) => {
  const displays = {
    INITIAL_EVALUATION: { label: 'Nutrition Review', color: 'warning' },
    COMPLEX_CASE_EVALUATION: { label: 'Nutrition Review', color: 'error' },
    WEEKLY_CHECKIN: { label: 'Weekly Check-in', color: 'success' },
  };
  return displays[type] || { label: type, color: 'default' };
};

// User-facing thread type display names (for messages shown to users)
export const getThreadTypeUserDisplay = (type) => {
  const displays = {
    INITIAL_EVALUATION: 'Your Nutrition Review',
    COMPLEX_CASE_EVALUATION: 'Your Nutrition Review',
    WEEKLY_CHECKIN: 'Weekly Check-in',
  };
  return displays[type] || 'Message';
};

export default api;
